import type { ReactNode } from "react";

import { Chart, ChartFrame, parseChartSpec } from "@/components/charts";
import { parsePlanSpec } from "./kaiTypes";
import { KaiPlanProgress } from "./KaiPlanProgress";
import { KaiDashboardCard, parseDashboardCardSpec } from "./KaiDashboardCard";
import { KaiRateComparison, parseRateComparisonSpec } from "./KaiRateComparison";
import { KaiRateComparisonRef } from "./KaiRateComparisonRef";
import { KaiFeedSnapshot } from "./KaiFeedSnapshot";
import { KaiFeedSnapshotRef } from "./KaiFeedSnapshotRef";
import { parseFeedSnapshotRefSpec, parseFeedSnapshotSpec } from "./kaiFeedTypes";
import { KaiMailReviewSnapshot } from "./KaiMailReviewSnapshot";
import { KaiMailReviewSnapshotRef } from "./KaiMailReviewSnapshotRef";
import {
  parseMailReviewSnapshotRefSpec,
  parseMailReviewSnapshotSpec,
} from "./kaiMailReviewTypes";
import type { KaiMailReviewContext } from "@/services/kaiApi";
import { KaiScheduleSnapshot } from "./KaiScheduleSnapshot";
import { KaiScheduleSnapshotRef } from "./KaiScheduleSnapshotRef";
import {
  parseScheduleSnapshotRefSpec,
  parseScheduleSnapshotSpec,
} from "./kaiScheduleTypes";
import { KaiScheduledReportsSnapshot } from "./KaiScheduledReportsSnapshot";
import { KaiScheduledReportsSnapshotRef } from "./KaiScheduledReportsSnapshotRef";
import {
  parseScheduledReportsSnapshotRefSpec,
  parseScheduledReportsSnapshotSpec,
} from "./kaiScheduledReportsTypes";
import { KaiActionProposal, parseActionProposalSpec } from "./KaiActionProposal";
import { KaiGuidance, parseGuidanceSpec } from "./KaiGuidance";
import { KaiBlockLoading } from "./KaiBlockLoading";
import { KaiBlockError } from "./KaiBlockError";

/**
 * Registry of every structured fenced-block Kai can emit inside a markdown
 * answer. The markdown renderer consults this registry for each fenced block
 * instead of a hard-coded if/else chain, so future block types plug in here.
 *
 * ── Adding a new block ─────────────────────────────────────────────────────
 * 1. Build the component + a `parse*Spec(body: string) => Spec | null` guard.
 * 2. Add a `register({...})` entry below with a `loadingLabel`.
 * 3. Add the matching interface to `kaiTypes.ts`.
 */

/** Context threaded through the renderer to each block's `render`. */
export interface KaiRenderCtx {
  /** True while the answer is still streaming (unclosed / partial blocks). */
  isStreaming?: boolean;
  /**
   * Server-validated artifact payloads keyed by tag, delivered ahead of the
   * final text via the `artifact` SSE event. Preferred over re-parsing the
   * fenced body when present.
   */
  artifacts?: Record<string, unknown>;
  /** Send a block's practical offer back to Kai as a follow-up message. */
  onSendFollowUp?: (text: string) => void;
  /**
   * Drill into a single email from a mail-review card: seed the active email
   * (so Kai opens it with `get_graph_mail_context`) and send a short prompt.
   */
  onReviewEmail?: (ctx: KaiMailReviewContext, text: string) => void;
  /** Run before an internal navigation (e.g. close the Kai rail). */
  beforeNavigate?: () => void;
  /** Whether to mask sensitive tokens (currently unused by these blocks). */
  maskSensitive?: boolean;
  /**
   * The conversation being rendered. Lets ID-referenced blocks (e.g.
   * `rate_comparison_ref`) fetch their stored payload on reload.
   */
  conversationId?: string | null;
}

export interface KaiFenceBlockDef<T = unknown> {
  tag: string;
  /** Shown by `KaiBlockLoading` while the block streams in. */
  loadingLabel: string;
  /** Parse + validate the fenced body. Returns `null` on any shape mismatch. */
  parse: (body: string) => T | null;
  render: (spec: T, ctx: KaiRenderCtx, key: number) => ReactNode;
}

const REGISTRY: Record<string, KaiFenceBlockDef<unknown>> = {};

function register<T>(def: KaiFenceBlockDef<T>): void {
  REGISTRY[def.tag] = def as KaiFenceBlockDef<unknown>;
}

register({
  tag: "chart",
  loadingLabel: "Building chart…",
  parse: parseChartSpec,
  render: (spec, _ctx, key) => (
    <ChartFrame key={key} variant="compact" title={spec.title} className="chart-animate">
      <Chart spec={spec} variant="compact" />
    </ChartFrame>
  ),
});

register({
  tag: "guidance",
  loadingLabel: "Preparing guidance…",
  parse: parseGuidanceSpec,
  render: (spec, ctx, key) => (
    <KaiGuidance
      key={key}
      spec={spec}
      onSendFollowUp={ctx.onSendFollowUp}
      beforeNavigate={ctx.beforeNavigate}
    />
  ),
});

register({
  tag: "rate_comparison",
  loadingLabel: "Building rate comparison…",
  parse: parseRateComparisonSpec,
  render: (spec, _ctx, key) => <KaiRateComparison key={key} spec={spec} />,
});

/**
 * A reference to a deterministically-built `rate_comparison` artifact persisted
 * server-side. The fence body carries `{ "id": "..." }`; the SSE artifact
 * payload additionally carries the full card under `artifact` for instant live
 * rendering. On reload the component fetches the payload by ID.
 */
interface RateComparisonRefSpec {
  id: string;
  artifact?: unknown;
}

register<RateComparisonRefSpec>({
  tag: "rate_comparison_ref",
  loadingLabel: "Loading rate comparison…",
  parse: (body) => {
    try {
      const parsed = JSON.parse(body);
      if (!parsed || typeof parsed.id !== "string" || !parsed.id) return null;
      return { id: parsed.id, artifact: parsed.artifact };
    } catch {
      return null;
    }
  },
  render: (spec, ctx, key) => (
    <KaiRateComparisonRef
      key={key}
      artifactId={spec.id}
      conversationId={ctx.conversationId}
      cachedArtifact={spec.artifact}
    />
  ),
});

register({
  tag: "feed_snapshot",
  loadingLabel: "Loading feed…",
  parse: parseFeedSnapshotSpec,
  render: (spec, _ctx, key) => <KaiFeedSnapshot key={key} spec={spec} />,
});

register({
  tag: "feed_snapshot_ref",
  loadingLabel: "Loading feed…",
  parse: parseFeedSnapshotRefSpec,
  render: (spec, ctx, key) => (
    <KaiFeedSnapshotRef
      key={key}
      artifactId={spec.id}
      conversationId={ctx.conversationId}
      cachedArtifact={spec.artifact}
    />
  ),
});

register({
  tag: "mail_review_snapshot",
  loadingLabel: "Loading mail review…",
  parse: parseMailReviewSnapshotSpec,
  render: (spec, ctx, key) => (
    <KaiMailReviewSnapshot
      key={key}
      spec={spec}
      onReviewEmail={ctx.onReviewEmail}
    />
  ),
});

register({
  tag: "mail_review_snapshot_ref",
  loadingLabel: "Loading mail review…",
  parse: parseMailReviewSnapshotRefSpec,
  render: (spec, ctx, key) => (
    <KaiMailReviewSnapshotRef
      key={key}
      artifactId={spec.id}
      conversationId={ctx.conversationId}
      cachedArtifact={spec.artifact}
      onReviewEmail={ctx.onReviewEmail}
    />
  ),
});

register({
  tag: "schedule_snapshot",
  loadingLabel: "Loading schedule…",
  parse: parseScheduleSnapshotSpec,
  render: (spec, _ctx, key) => <KaiScheduleSnapshot key={key} spec={spec} />,
});

register({
  tag: "schedule_snapshot_ref",
  loadingLabel: "Loading schedule…",
  parse: parseScheduleSnapshotRefSpec,
  render: (spec, ctx, key) => (
    <KaiScheduleSnapshotRef
      key={key}
      artifactId={spec.id}
      conversationId={ctx.conversationId}
      cachedArtifact={spec.artifact}
    />
  ),
});

register({
  tag: "scheduled_reports_snapshot",
  loadingLabel: "Loading scheduled reports…",
  parse: parseScheduledReportsSnapshotSpec,
  render: (spec, ctx, key) => (
    <KaiScheduledReportsSnapshot
      key={key}
      spec={spec}
      beforeNavigate={ctx.beforeNavigate}
    />
  ),
});

register({
  tag: "scheduled_reports_snapshot_ref",
  loadingLabel: "Loading scheduled reports…",
  parse: parseScheduledReportsSnapshotRefSpec,
  render: (spec, ctx, key) => (
    <KaiScheduledReportsSnapshotRef
      key={key}
      artifactId={spec.id}
      conversationId={ctx.conversationId}
      cachedArtifact={spec.artifact}
      beforeNavigate={ctx.beforeNavigate}
    />
  ),
});

register({
  tag: "action_proposal",
  loadingLabel: "Preparing action…",
  parse: parseActionProposalSpec,
  render: (spec, _ctx, key) => <KaiActionProposal key={key} spec={spec} />,
});

register({
  tag: "plan",
  loadingLabel: "Planning the steps…",
  parse: parsePlanSpec,
  render: (spec, _ctx, key) => <KaiPlanProgress key={key} spec={spec} />,
});

register({
  tag: "dashboard_card",
  loadingLabel: "Building dashboard…",
  parse: parseDashboardCardSpec,
  render: (spec, _ctx, key) => <KaiDashboardCard key={key} spec={spec} />,
});

/** Whether `tag` denotes a registered structured block. */
export function isFenceBlockTag(tag: string): boolean {
  return tag in REGISTRY;
}

/**
 * Map a generic ` ```json ` fence to a structured artifact tag when the body
 * validates as one. Mirrors the v1 coalescing so Kai answers that emit an
 * untagged JSON block still render as rich cards.
 */
export function coalesceFenceTag(tag: string, body: string): string {
  if (isFenceBlockTag(tag) || tag !== "json") return tag;
  if (parseActionProposalSpec(body)) return "action_proposal";
  if (parseRateComparisonSpec(body)) return "rate_comparison";
  if (parseDashboardCardSpec(body)) return "dashboard_card";
  return tag;
}

/**
 * Render a fenced block for the given tag. Returns:
 *   - the structured component when the body (or SSE payload) parses,
 *   - a `KaiBlockLoading` placeholder while streaming and not yet parseable,
 *   - a `KaiBlockError` note once streaming has finished but parsing failed,
 *   - `null` when `tag` is not a registered structured block.
 */
export function renderFenceBlock(
  tag: string,
  body: string,
  ctx: KaiRenderCtx,
  key: number,
): ReactNode | null {
  const def = REGISTRY[tag];
  if (!def) return null;

  let spec: unknown = null;

  const payload = ctx.artifacts?.[tag];
  if (payload != null) {
    try {
      spec = def.parse(JSON.stringify(payload));
    } catch {
      spec = null;
    }
  }

  if (spec == null) {
    spec = def.parse(body);
  }

  if (spec != null) {
    return def.render(spec, ctx, key);
  }

  if (ctx.isStreaming) {
    return <KaiBlockLoading key={key} label={def.loadingLabel} />;
  }

  return <KaiBlockError key={key} tag={tag} />;
}
